import { useState, useCallback, useEffect } from "react";
import {
  getMyProfile,
  updateMyProfile,
  changePassword as changePwService,
  revokeSession as revokeSessionService,
  revokeAllSessions as revokeAllSessionsService,
  sendPhoneVerificationOTP as sendOTPService,
  verifyPhoneOTP as verifyOTPService,
} from "services/profileService";
import type {
  ProfileUpdatePayload,
  ChangePasswordPayload,
} from "services/profileService";
import { useToast } from "Components/Common/Toast/ToastContext";
import { uploadFileToS3 } from "Pages/GcPrecon/GcPreconCommercial/UploadFileToS3";

export interface SessionData {
  id: string;
  icon: string;
  device: string;
  location: string;
  lastActive: string;
  isCurrent: boolean;
}

export interface ProfileData {
  firstName: string;
  lastName: string;
  title: string;
  organization: string;
  email: string;
  phone: string;
  address: string;
  timezone: string;
  role: string;
  phoneVerified: boolean;
  sms2faEnabled: boolean;
  emailLoginAlertEnabled: boolean;
  smsNotificationsEnabled: boolean;
  emailNotificationsEnabled: boolean;
  activeSessions: SessionData[];
  avatar: string | null;
}

interface RawSession {
  id?: string;
  deviceName?: string | null;
  userAgent?: string;
  location?: string | null;
  lastActiveAt?: string;
  isCurrent?: boolean;
}

interface RawProfile {
  firstName?: string;
  lastName?: string;
  title?: string;
  email?: string;
  phone?: string;
  address?: string;
  timeZone?: string;
  organization?: string;
  company?: string;
  companyName?: string;
  roles?: string[];
  role?: string;
  phoneVerifiedAt?: string | null;
  phoneVerified?: boolean;
  sms2faEnabled?: boolean;
  emailLoginAlertEnabled?: boolean;
  activeSessions?: RawSession[];
  notificationPreferences?: {
    emailEnabled?: boolean;
    smsEnabled?: boolean;
    inAppEnabled?: boolean;
    smsConsentStatus?: string;
  };
  avatar?: { url?: string; key?: string } | string | null;
}

function formatRelativeTime(dateStr: string): string {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days > 1 ? "s" : ""} ago`;
}

function normalizeSession(raw: RawSession): SessionData {
  const ua = raw.userAgent ?? "";
  const isMobile = /mobile|iphone|android/i.test(ua);
  return {
    id: raw.id ?? "",
    icon: isMobile ? "📱" : "💻",
    device: raw.deviceName ?? "Unknown Device",
    location: raw.location ?? "",
    lastActive: raw.lastActiveAt ? formatRelativeTime(raw.lastActiveAt) : "—",
    isCurrent: raw.isCurrent ?? false,
  };
}

function normalizeProfile(raw: RawProfile): ProfileData {
  return {
    firstName: raw.firstName ?? "",
    lastName: raw.lastName ?? "",
    title: raw.title ?? "",
    organization:
      raw.companyName ?? raw.organization ?? raw.company ?? "CSC New England",
    email: raw.email ?? "",
    phone: raw.phone ?? "",
    address: raw.address ?? "",
    timezone: raw.timeZone ?? "Eastern Time (ET)",
    role: (() => {
      const active = localStorage.getItem("role") ?? "";
      return raw.roles?.includes(active)
        ? active
        : (raw.roles?.[0] ?? raw.role ?? active);
    })(),
    phoneVerified:
      raw.phoneVerifiedAt != null ? true : (raw.phoneVerified ?? false),
    sms2faEnabled: raw.sms2faEnabled ?? true,
    emailLoginAlertEnabled: raw.emailLoginAlertEnabled ?? true,
    smsNotificationsEnabled: raw.notificationPreferences?.smsEnabled ?? false,
    emailNotificationsEnabled:
      raw.notificationPreferences?.emailEnabled ?? true,
    activeSessions: (raw.activeSessions ?? []).map(normalizeSession),
    avatar: raw.avatar
      ? typeof raw.avatar === "string"
        ? raw.avatar
        : (raw.avatar.url ?? null)
      : null,
  };
}

const empty = normalizeProfile({});

export default function useProfile() {
  const showToast = useToast();
  const [profile, setProfile] = useState<ProfileData>(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [sendingOTP, setSendingOTP] = useState(false);
  const [verifyingOTP, setVerifyingOTP] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const fetchProfile = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      const { data, error } = await getMyProfile();
      if (!silent) setLoading(false);
      if (error) {
        if (!silent) showToast(error.message ?? "Failed to load profile");
        return;
      }
      const raw: RawProfile = (data as any)?.data ?? (data as any) ?? {};
      const normalized = normalizeProfile(raw);
      setProfile(normalized);

      try {
        const stored = JSON.parse(localStorage.getItem("user") || "{}");
        const currentUrl =
          typeof stored.avatar === "object"
            ? (stored.avatar?.url ?? null)
            : (stored.avatar ?? null);
        const updatedStored: Record<string, unknown> = {
          ...stored,
          phone: normalized.phone || stored.phone,
          phoneVerifiedAt: raw.phoneVerifiedAt ?? null,
        };
        let dispatchAvatar = false;
        if (normalized.avatar !== currentUrl) {
          updatedStored.avatar = normalized.avatar
            ? {
                ...(typeof stored.avatar === "object" ? stored.avatar : {}),
                url: normalized.avatar,
              }
            : null;
          dispatchAvatar = true;
        }
        localStorage.setItem("user", JSON.stringify(updatedStored));
        if (dispatchAvatar) window.dispatchEvent(new Event("avatar-changed"));
      } catch {}
    },
    [showToast],
  );

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const saveProfile = useCallback(
    async (values: ProfileUpdatePayload): Promise<boolean> => {
      setSaving(true);
      const { error } = await updateMyProfile(values);
      setSaving(false);
      if (error) {
        showToast(error.message ?? "Failed to update profile");
        return false;
      }
      await fetchProfile();
      showToast("Profile updated successfully");
      return true;
    },
    [showToast, fetchProfile],
  );

  const changePassword = useCallback(
    async (data: ChangePasswordPayload): Promise<boolean> => {
      setChangingPassword(true);
      const { error } = await changePwService(data);
      setChangingPassword(false);
      if (error) {
        showToast(error.message ?? "Failed to update password");
        return false;
      }
      showToast("Password updated successfully");
      return true;
    },
    [showToast],
  );

  const revokeSession = useCallback(
    async (sessionId: string): Promise<void> => {
      const { error } = await revokeSessionService(sessionId);
      if (error) {
        showToast(error.message ?? "Failed to revoke session");
        return;
      }
      setProfile((prev) => ({
        ...prev,
        activeSessions: prev.activeSessions.filter((s) => s.id !== sessionId),
      }));
      showToast("Session revoked");
    },
    [showToast],
  );

  const revokeAllSessions = useCallback(async (): Promise<void> => {
    const { error } = await revokeAllSessionsService();
    if (error) {
      showToast(error.message ?? "Failed to revoke sessions");
      return;
    }
    setProfile((prev) => ({
      ...prev,
      activeSessions: prev.activeSessions.filter((s) => s.isCurrent),
    }));
    showToast("All other sessions revoked");
  }, [showToast]);

  const updatePhone = useCallback(
    async (phone: string): Promise<boolean> => {
      const { error } = await updateMyProfile({ phone });
      if (error) {
        showToast(error.message ?? "Failed to update phone number");
        return false;
      }
      try {
        const stored = JSON.parse(localStorage.getItem("user") || "{}");
        localStorage.setItem(
          "user",
          JSON.stringify({ ...stored, phone, phoneVerifiedAt: null }),
        );
      } catch {}
      return true;
    },
    [showToast],
  );

  const sendPhoneVerificationOTP = useCallback(
    async (phone: string): Promise<boolean> => {
      setSendingOTP(true);
      const phoneUpdated = await updatePhone(phone);
      if (!phoneUpdated) {
        setSendingOTP(false);
        return false;
      }
      await fetchProfile(true);
      const { error } = await sendOTPService();
      setSendingOTP(false);
      if (error) {
        showToast(error.message ?? "Failed to send OTP");
        return false;
      }
      showToast("OTP sent to your phone number");
      return true;
    },
    [showToast, fetchProfile, updatePhone],
  );

  const verifyPhoneOTP = useCallback(
    async (otp: string): Promise<boolean> => {
      setVerifyingOTP(true);
      const { error } = await verifyOTPService(otp);
      setVerifyingOTP(false);
      if (error) {
        showToast(error.message ?? "Invalid or expired OTP");
        return false;
      }
      showToast("Phone number verified successfully");
      await fetchProfile();
      return true;
    },
    [showToast, fetchProfile],
  );

  const uploadAvatar = useCallback(
    async (file: File): Promise<boolean> => {
      setUploadingAvatar(true);
      try {
        const { key } = await uploadFileToS3(file);
        const { error } = await updateMyProfile({
          avatar: { key, fileName: file.name },
        });
        if (error) {
          showToast(error.message ?? "Failed to update profile photo");
          return false;
        }
        await fetchProfile();
        showToast("Profile photo updated");
        return true;
      } catch (err) {
        showToast(
          err instanceof Error ? err.message : "Upload failed",
          "error",
        );
        return false;
      } finally {
        setUploadingAvatar(false);
      }
    },
    [showToast, fetchProfile],
  );

  const updateNotificationChannels = useCallback(
    async (updates: {
      emailEnabled?: boolean;
      smsEnabled?: boolean;
    }): Promise<boolean> => {
      setSavingNotifications(true);
      const { error } = await updateMyProfile(updates);
      setSavingNotifications(false);
      if (error) {
        showToast(
          error.message ?? "Failed to update notification preferences",
          "error",
        );
        return false;
      }
      await fetchProfile();
      showToast("Notification preferences updated");
      return true;
    },
    [showToast, fetchProfile],
  );

  return {
    profile,
    loading,
    saving,
    changingPassword,
    sendingOTP,
    verifyingOTP,
    savingNotifications,
    uploadingAvatar,
    saveProfile,
    changePassword,
    revokeSession,
    revokeAllSessions,
    updatePhone,
    sendPhoneVerificationOTP,
    verifyPhoneOTP,
    updateNotificationChannels,
    uploadAvatar,
    refetchProfile: fetchProfile,
  };
}
